/**
 * Errors that reach `window.onerror` but are not ours to act on.
 *
 * This list is shared on purpose. `GlobalErrorHandler` listens with
 * `addEventListener`, while posthog-js's exception autocapture
 * (`capture_exceptions`) monkey-patches `window.onerror` directly and never
 * passes through it — so a filter applied in only one of them silences the
 * toast but still reports the event, or vice versa. Both import from here.
 */

/** Cancelamentos esperados (navegação rápida cancela fetches em andamento). */
export function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") return true
  if (error instanceof Error && error.name === "AbortError") return true
  return isAbortMessage(messageOf(error))
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function isAbortMessage(message: string): boolean {
  return (
    message.includes("The user aborted a request") ||
    message.includes("signal is aborted") ||
    message.includes("The operation was aborted")
  )
}

/**
 * Artefato do browser, não erro da aplicação: o callback de um ResizeObserver
 * alterou o layout e reagendou o próprio observer. O Chrome despacha isso como
 * um `error` event de verdade. Nenhum componente nosso instancia
 * ResizeObserver — vem do `useSize`/Popper do Radix (Select, Popover,
 * DropdownMenu, Tooltip), então não há callback nosso para envolver em rAF.
 */
function isResizeObserverLoop(message: string): boolean {
  return message.startsWith("ResizeObserver loop")
}

/**
 * Navegador in-app do Android (Instagram/Facebook) injeta a própria
 * instrumentação de Web Vitals via `evaluateJavascript` e ela chama um objeto
 * Java que já foi destruído quando o usuário sai da tela. Código de terceiro
 * rodando na nossa página: não existe `sendDataToNative` neste repositório.
 */
function isInjectedBridgeError(message: string): boolean {
  return message.includes("Java object is gone")
}

/** Decide pela mensagem — é o que o `before_send` do posthog-js enxerga. */
export function isIgnoredErrorMessage(message: string): boolean {
  return (
    isAbortMessage(message) ||
    isResizeObserverLoop(message) ||
    isInjectedBridgeError(message)
  )
}

export function isIgnoredError(error: unknown): boolean {
  return isAbortError(error) || isIgnoredErrorMessage(messageOf(error))
}
